import { useState } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import { TApi, TSupporterRoles } from '../../../../shared/const'
import http from '../../../../shared/http'
import useDialogDismiss from '../../../../shared/useDialogDismiss'
import useIsDirty from '../../../../shared/useIsDirty'
import { askDiscardChanges } from '../../../../shared/confirmDialog'

import Btn from '../../../../UI/Btn'
import { PlainInput, PlainCheckbox } from '../../../../UI/PlainField'

import styles from './inviteModal.module.scss'

const EMPTY = {
	email: '',
	firstName: '',
	lastName: '',
	company: '',
	position: ''
}

/**
 * Einladung einer Unterstuetzer:in durch die Administration.
 *
 * Die gewaehlten Sub-Rollen sind ein Vorschlag: die eingeladene Person bestaetigt
 * oder aendert sie beim Onboarding selbst (Spec Teil 1, Punkt 3).
 */
export default function InviteSupporterModal({ show, onClose, onInvited }) {
	const { t } = useTranslation()

	const [values, setValues] = useState(EMPTY)
	const [roles, setRoles] = useState([TSupporterRoles.EXPERT])
	const [isSaving, setSaving] = useState(false)
	const [errors, setErrors] = useState({})

	const isDirty = useIsDirty(show ? { values, roles } : null)

	const requestClose = async () => {
		if (isDirty && !(await askDiscardChanges())) return

		// Beim naechsten Oeffnen soll ein leeres Formular dastehen und nicht das
		// eben Verworfene.
		setValues(EMPTY)
		setRoles([TSupporterRoles.EXPERT])
		setErrors({})
		onClose()
	}

	const backdropProps = useDialogDismiss(requestClose, { enabled: show })

	if (!show) return null

	const setValue = (key, value) => setValues(current => ({ ...current, [key]: value }))

	const toggleRole = role =>
		setRoles(current =>
			current.includes(role) ? current.filter(item => item !== role) : [...current, role]
		)

	const validate = () => {
		const next = {}
		if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email)) next.email = t('Invalid email')
		if (values.firstName.trim().length < 2) next.firstName = t('Required field')
		if (values.lastName.trim().length < 2) next.lastName = t('Required field')
		if (!roles.length) next.roles = t('Required field')

		setErrors(next)
		return !Object.keys(next).length
	}

	const submit = event => {
		event.preventDefault()
		if (!validate()) return

		setSaving(true)
		http.post(TApi.ADMIN_SUPPORTER_INVITATIONS, { ...values, roles })
			.then(({ data }) => {
				// Der Server meldet zurueck, ob die E-Mail wirklich raus ist. Eine
				// angelegte Einladung ohne versendete Mail waere sonst unbemerkt.
				if (data?.emailSent === false) {
					toast.warning(t('Invitation saved, but the email could not be sent'))
				} else {
					toast.success(t('Saved successfully'))
				}
				setValues(EMPTY)
				setRoles([TSupporterRoles.EXPERT])
				onInvited()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	return (
		<div className={styles.overlay} {...backdropProps}>
			<form className={styles.modal} onSubmit={submit}>
				<h2 className={styles.title}>{t('inviteSupporter')}</h2>

				<div className={styles.grid}>
					<PlainInput
						label={t('firstName')}
						value={values.firstName}
						error={errors.firstName}
						onChange={e => setValue('firstName', e.target.value)}
					/>
					<PlainInput
						label={t('lastName')}
						value={values.lastName}
						error={errors.lastName}
						onChange={e => setValue('lastName', e.target.value)}
					/>
					<PlainInput
						label={t('email')}
						value={values.email}
						error={errors.email}
						onChange={e => setValue('email', e.target.value)}
					/>
					<PlainInput
						label={t('company')}
						value={values.company}
						onChange={e => setValue('company', e.target.value)}
					/>
					<PlainInput
						label={t('position')}
						value={values.position}
						onChange={e => setValue('position', e.target.value)}
					/>
				</div>

				<div className={styles.roles}>
					<span className={styles.rolesLabel}>{t('possibleRoles')}</span>
					<div className={styles.roleList}>
						{Object.values(TSupporterRoles).map(role => (
							<PlainCheckbox
								key={role}
								checked={roles.includes(role)}
								onChange={() => toggleRole(role)}
								label={t(`supporterRole${role}`)}
							/>
						))}
					</div>
					{errors.roles && <span className={styles.error}>{errors.roles}</span>}
				</div>

				<div className={styles.footer}>
					<Btn type="button" theme="secondary" onClick={requestClose}>
						{t('Cancel')}
					</Btn>
					<Btn type="submit" disabled={isSaving}>
						{t('inviteSupporter')}
					</Btn>
				</div>
			</form>
		</div>
	)
}
